import { BehaviorSubject, Observable } from 'rxjs';
import { produce, Draft } from 'immer';

import type { MinMaxType } from '@nf/types/common';
import type { BookingValue, BookingValueLimit } from '@/types/booked-bet';
import { BookingErrorType } from '@/types/booked-bet';
import BookingManager from '../services/booking-strategies/booking-manager';
import { WebOddsType, IncDecType } from '@nf/utils-common/constants';
import Bloc from '@nf/utils-common/models/bloc';
import { itemsEmptyCheck } from '@nf/utils-common/fpw/utils';
import { idealRound, isRangeExcludingValue } from '@nf/utils-common/numbers';
import { reservationService } from '../services/reservation.service';

interface BookingOptions {
	betType: number;
	oddsType: WebOddsType;
	originalValue: number;
	minMaxType: MinMaxType;
}

abstract class BookingValueBloc implements Bloc {
	protected _bookingValue$: BehaviorSubject<BookingValue>;
	protected bookingOptions: BookingOptions = {} as BookingOptions;
	protected bookingManager!: BookingManager;

	constructor() {
		this._bookingValue$ = new BehaviorSubject<BookingValue>({
			expectedValue: 0,
			errorType: 0,
			isOddsTypeMinValue: false,
			isOddsTypeMaxValue: false,
			isVerified: false,
		});
	}

	public get bookingValue$(): Observable<BookingValue> {
		return this._bookingValue$.asObservable();
	}

	public getBookingValue(): BookingValue {
		return this._bookingValue$.getValue();
	}

	protected abstract configureStrategy(): void;

	public abstract plus(): void;

	public abstract minus(): void;

	public abstract calculateDefaultIncrementedLimit(value: number): BookingValueLimit;

	public abstract calculateDefaultDecrementedLimit(value: number): BookingValueLimit;

	public setInititalValue(value: number) {
		const validateResult = this.validateLimitByOddsType(value);

		this._bookingValue$.next({
			expectedValue: validateResult.value,
			errorType: 0,
			isOddsTypeMinValue: validateResult.isOddsTypeMinValue,
			isOddsTypeMaxValue: validateResult.isOddsTypeMaxValue,
			isVerified: false,
		});
		document.dispatchEvent(new CustomEvent('booking-initialized'));
	}

	public setBookingOptions(options: BookingOptions) {
		this.bookingOptions = {
			betType: options.betType,
			oddsType: options.oddsType,
			originalValue: options.originalValue,
			minMaxType: options.minMaxType,
		};
		this.configureStrategy();
	}

	protected get info() {
		const { betType, oddsType } = this.bookingOptions;

		return {
			isHandicap: [1, 7].includes(betType),
			isOU: [3, 8].includes(betType),
			isMalayOdds: oddsType === WebOddsType.MY,
			isDecimalOdds: oddsType === WebOddsType.DEC,
			isFixedDecimal: reservationService.fixedDecimalBetTypes.includes(betType),
		};
	}

	private checkVerifyInfoWithDraft(draft: Draft<BookingValue>, value: number) {
		const validateResult = this.validateLimitByOddsType(value);
		draft.isOddsTypeMinValue = validateResult.isOddsTypeMinValue;
		draft.isOddsTypeMaxValue = validateResult.isOddsTypeMaxValue;
		draft.expectedValue = validateResult.value;
	}

	private resetBookingErrorWithDraft(draft: Draft<BookingValue>) {
		draft.errorType = 0;
		draft.isVerified = false;
	}

	protected checkAndResetBookingValueError(draft: Draft<BookingValue>, value: number) {
		this.checkVerifyInfoWithDraft(draft, value);
		this.resetBookingErrorWithDraft(draft);
	};

	protected validateLimitByOddsType(valueToValidate: number) {
		let value = valueToValidate;
		let isOddsTypeMinValue = false;
		let isOddsTypeMaxValue = false;
		const { min, max } = this.bookingManager.getOddsTypeLimit(value);

		if (min !== undefined && value <= min) {
			value = min;
			isOddsTypeMinValue = true;
		};

		if (max !== undefined && value >= max) {
			value = max;
			isOddsTypeMaxValue = true;
		};
		
		return { 
			value: idealRound(value, 2), 
			isOddsTypeMinValue, 
			isOddsTypeMaxValue 
		};
	}

	private validateRange(
		rangeToValidate: [number, number], 
		decrementalRange: Array<number>, 
		incrementalRange: Array<number>
	) {
		const { originalValue } = this.bookingOptions;
		// Create a range containing the original values
		const inclusiveRange = [...decrementalRange, originalValue, ...incrementalRange];
		const originalValueIdx = inclusiveRange.indexOf(originalValue);

		const [start, end] = rangeToValidate;
		const startIdx = inclusiveRange.indexOf(start);
		const endIdx = inclusiveRange.indexOf(end);

		return isRangeExcludingValue(startIdx, endIdx)(originalValueIdx);
	}

	public validate(pairValue: number) {
		const { originalValue, minMaxType } = this.bookingOptions;
		const isMinType = minMaxType === 'min';

		const decrementalRange = this.bookingManager.calculateDefaultRange(originalValue, IncDecType.Decrement);
		const incrementalRange = this.bookingManager.calculateDefaultRange(originalValue, IncDecType.Increment);
		// Range excluding the original values
		const validRange = [...decrementalRange, ...incrementalRange];

		const incrementalLimit = {
			min: incrementalRange[0],
			max: incrementalRange[incrementalRange.length - 1]
		};
		
		this._bookingValue$.next(
			produce(this._bookingValue$.getValue(), draft => {
				let value = draft.expectedValue;
				let errorType = 0;

				itemsEmptyCheck(validRange).fork({
					just: () => {
						const rangeToValidate: [number, number] = isMinType 
							? [value, pairValue] 
							: [pairValue, value];
						const isPairInRange = rangeToValidate.every(v => validRange.includes(v));
				
						// Swapping the values ensures correct ordering
						if (isPairInRange) {
							const isSameSign = (value >= 0 && pairValue >= 0) || (value < 0 && pairValue < 0);
							rangeToValidate.sort((a, b) => isSameSign ? a - b : b - a);
							value = rangeToValidate[isMinType ? 0 : 1];
						}

						// Validate whether it is within a valid range (excluding the original values)
						const isWithinValidRange = this.validateRange(
							rangeToValidate, 
							decrementalRange,
							incrementalRange
						);

						if ((isPairInRange && !isWithinValidRange) || value === originalValue) {
							errorType = BookingErrorType.IntervalRange;
							// Reset to the minimum / maximum value of the default range
							value = incrementalLimit[minMaxType];
						} else if (!validRange.includes(value)) {
							errorType = isMinType ? BookingErrorType.Minimum : BookingErrorType.Maximum;
							value = isMinType ? incrementalLimit.min : incrementalLimit.max;
						}
					},
					none: () => {
						const { min, max } = this.bookingManager.getOddsTypeLimit(value);

						if (min !== undefined && value < min) {
							errorType = BookingErrorType.Minimum;
						} else if (max !== undefined && value > max) {
							errorType = BookingErrorType.Maximum;
						}
					}
				});

				// Set verify status by odds type
				this.checkVerifyInfoWithDraft(draft, value);

				draft.errorType = errorType;
				draft.isVerified = errorType === 0;
			})
		);
	};

	public resetBookingError() {
		this._bookingValue$.next(
			produce(this._bookingValue$.getValue(), this.resetBookingErrorWithDraft)
		);
	}

	dispose(): void {
		this._bookingValue$.complete();
	}
}

export default BookingValueBloc;